import { Component, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { map, tap, switchMap, share, shareReplay } from 'rxjs/operators';
import { Invoice as LegacyInvoice } from '../../model/invoice';
import { CreditInsurancesResource } from '../../model/model-resource';
import { combineLatest, Observable, of, } from 'rxjs';
import { EndCustomerPeerService } from 'src/app/service/end-customer-peer.service';
import { CreditInsurance as LegacyCreditInsurance } from 'src/app/model/credit-insurance';
import dayjs from 'dayjs';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatTable } from '@angular/material/table';
import { CurrencyPipe } from '@angular/common'
import { CreditInsurance, EndCustomer, Invoice, Money as IMoney } from 'src/app/api/models';
import currency from 'currency.js';
import { Money } from 'src/app/model/money';
import { InvoicesService } from 'src/app/api/services';
import { MoneyPipe } from 'src/app/pipe/money.pipe';
import { InvoiceCalculatorService } from 'src/app/service/invoice-calculator/invoice-calculator.service';
import { InvoiceNotificationsService } from 'src/app/service/invoice-notifications.service';

class Summary {
  count: number
  money: IMoney
}

@Component({
  selector: 'app-invoice-end-customer-credit-insurance-widget',
  templateUrl: './invoice-end-customer-credit-insurance-widget.component.html',
  styleUrls: ['./invoice-end-customer-credit-insurance-widget.component.scss']
})
export class InvoiceEndCustomerCreditInsuranceWidgetComponent implements OnInit {
  @Output() hasData = new EventEmitter<boolean>();
  @ViewChild(MatTable) private table!: MatTable<CreditInsurance>;

  endCustomer$: Observable<EndCustomer>;
  usedCreditInsuranceAmount$: Observable<Summary>;
  availableCreditInsuranceAmount$: Observable<IMoney>;
  currentCreditInsurance$: Observable<CreditInsurance>;

  columnsToDisplay = ['valid_from', 'amount', 'note', 'actions'];

  showCreditInsurances = false;

  subtitle$: Observable<string>;

  form: UntypedFormGroup;
  locked = false;
  added = false;
  saving = false;

  constructor(
    private invoicePeerService: InvoicePeerService,
    private endCustomerPeerService: EndCustomerPeerService,
    private creditInsurancesResource: CreditInsurancesResource,
    private invoicesService: InvoicesService,
    private invoiceNotificationsService: InvoiceNotificationsService,
    private moneyPipe: MoneyPipe,
    private invoiceCalculatorService: InvoiceCalculatorService,
    private currencyPipe: CurrencyPipe,
    private builder: UntypedFormBuilder
  ) {
    this.form = this.builder.group({
      end_customer_id: [],
      amount: this.builder.group({
        amount: [null, Validators.required],
        currency: ['EUR'],
      }),
      valid_from: [null, Validators.required],
      note: [null]
    });
  }

  add(): void {
    this.showCreditInsurances = true;
    this.table?.removeFooterRowDef(null);
    this.endCustomerPeerService.lock();
    this.added = true;
    //this.form.reset();
    this.form.patchValue({
      amount: {
        amount: null,
        currency: 'EUR',
      },
      valid_from: null,
      note: null
    });
  }

  revert(): void {
    this.table.removeFooterRowDef(null);
    this.invoicePeerService.unlock();
    this.added = false;
  }

  save(): void {
    console.log("saving");
    console.log(this.form.value);
    this.saving = true;
    const creditInsurance = <CreditInsurance>{};
    Object.assign(creditInsurance, this.form.value);
    const legacy = this.convertToLegacyCreditInsurance(creditInsurance);
    this.creditInsurancesResource.save(legacy).subscribe(() => {
      this.endCustomerPeerService.unlock();
      this.endCustomerPeerService.refresh();
      this.saving = false;
      this.added = false;
      this.table.removeFooterRowDef(null);
    });
  }

  convertToLegacyCreditInsurance(creditInsurance: CreditInsurance): LegacyCreditInsurance {
    const legacy = new LegacyCreditInsurance();
    legacy.creditInsuranceId = creditInsurance.id;
    legacy.endCustomerId = creditInsurance.end_customer_id;
    legacy.amount = currency(new Money(creditInsurance.amount).amount).value;
    legacy.validFrom = dayjs(creditInsurance.valid_from).toDate();
    if (creditInsurance.valid_to) {
      legacy.validTo = dayjs(creditInsurance.valid_from).toDate();
    }
    legacy.note = creditInsurance.note;

    return legacy;
  }

  toggleDetails(): void {
    this.showCreditInsurances = !this.showCreditInsurances;
  }

  deleteCreditInsurance(creditInsurance: CreditInsurance) {
    console.log("deleting");
    console.log(creditInsurance);
    this.endCustomerPeerService.lock();
    this.creditInsurancesResource.remove(creditInsurance.id).subscribe(() => {
      this.endCustomerPeerService.unlock();
      this.endCustomerPeerService.refresh();
      this.saving = false;
      this.added = false;
      this.table.removeFooterRowDef(null);
    });
  }

  ngOnInit() {
    this.endCustomer$ = this.endCustomerPeerService.get()
      .pipe(
        tap(endCustomer => this.form.patchValue({ end_customer_id: endCustomer.id })),
        tap(() => this.hasData.emit(true)),
        share()
      );

    this.currentCreditInsurance$ = this.endCustomerPeerService.get()
      .pipe(
        map(endCustomer => {
          return this.getCurrentInsurance(endCustomer.credit_insurances);
        }),
        share()
      );

    this.usedCreditInsuranceAmount$ = this.invoicePeerService
      .get()
      .pipe(
        switchMap((invoice: LegacyInvoice) => {
          if (invoice.endCustomerId !== null) {
            return this.invoicesService.getEndCustomerInvoices({ end_customer_id: invoice.endCustomerId })
              .pipe(
                map(page => {
                  return page.items;
                }),
                shareReplay(1));
          } else {
            return of([]);
          }
        }),
        map((invoices: Invoice[]) => {
          const d = this.invoiceCalculatorService.getInvoicesAndSummary(invoices);

          return {
            count: d.open.count,
            money: d.open.amount,
          }
        }),
        share()
      );

    this.availableCreditInsuranceAmount$ = combineLatest([
      this.currentCreditInsurance$,
      this.usedCreditInsuranceAmount$
    ]).pipe(
      map(([current, used]) => {
        if (current === null) {
          return null;
        }
        const c = new Money(current.amount).toDinero();
        const u = new Money(used.money).toDinero();
        const available = new Money(c.subtract(u))
        if (available.toDinero().isNegative()) {
          this.invoiceNotificationsService.setEndCustomerOverCreditInsuranceLimit(true);
        }

        return available;
      }),
      share()
    );

    this.subtitle$ = this.availableCreditInsuranceAmount$.pipe(
      map((available) => {
        return this.moneyPipe.transform(available, 'symbol', '1.2-2');
      }),
      share()
    );
  }

  private getCurrentInsurance(insurances: CreditInsurance[]) {
    if (!insurances) {
      return null;
    }

    const now = dayjs();
    return insurances.reduce((prev, current) => {
      if (dayjs(current.valid_from).isAfter(now)) {
        return prev;
      }
      if (prev === null) {
        return current
      }
      if (dayjs(current.valid_from).isAfter(prev.valid_from)) {
        return current;
      }
      return prev;
    }, null)
  }
}

